import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  useCancelReferral,
  useReferralDetail,
} from "../../../hooks/useReferral";

type StatusChipColor = "warning" | "success" | "default" | "error";

const statusColor = (status: string): StatusChipColor => {
  switch (status) {
    case "Pending": return "warning";
    case "Completed": return "success";
    case "Expired": return "default";
    case "Cancelled": return "error";
    default: return "default";
  }
};

interface LabelValueProps {
  label: string;
  value: React.ReactNode;
}

const LabelValue: React.FC<LabelValueProps> = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

interface ReferralViewModalProps {
  open: boolean;
  onClose: () => void;
  referralId: string | null;
}

const ReferralViewModal: React.FC<ReferralViewModalProps> = ({
  open,
  onClose,
  referralId,
}) => {
  const { t } = useTranslation();

  const { data, isLoading } = useReferralDetail(referralId ?? "");
  const cancelMutation = useCancelReferral();

  const detail = data?.data;

  const handleCancel = async () => {
    if (!referralId) return;
    await cancelMutation.mutateAsync(referralId);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("admin.referral.viewDetailTitle")}</DialogTitle>
      <DialogContent dividers>
        {isLoading || !detail ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Referrer */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("admin.referral.referrerInfo")}
              </Typography>
              <LabelValue label={t("admin.referral.name")} value={detail.referrerName} />
              <LabelValue label={t("admin.referral.email")} value={detail.referrerEmail} />
            </Grid>

            {/* Referee */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("admin.referral.refereeInfo")}
              </Typography>
              <LabelValue label={t("admin.referral.name")} value={detail.refereeName} />
              <LabelValue label={t("admin.referral.email")} value={detail.refereeEmail} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Status & Dates */}
            <Grid item xs={12} sm={6}>
              <LabelValue
                label={t("admin.referral.status")}
                value={
                  <Chip
                    label={t(`admin.referral.statusLabel.${detail.status.toLowerCase()}`)}
                    color={statusColor(detail.status)}
                    size="small"
                  />
                }
              />
              <LabelValue
                label={t("admin.referral.date")}
                value={new Date(detail.createdAt).toLocaleString()}
              />
              {detail.completedAt && (
                <LabelValue
                  label={t("admin.referral.completedDate")}
                  value={new Date(detail.completedAt).toLocaleString()}
                />
              )}
            </Grid>

            {/* Order */}
            {detail.orderNumber && (
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label={t("admin.referral.orderNumber")}
                  value={detail.orderNumber}
                />
              </Grid>
            )}

            {/* Reward codes (only when completed) */}
            {detail.status === "Completed" && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("admin.referral.referrerReward")}
                  </Typography>
                  {detail.referrerRewardCode ? (
                    <>
                      <LabelValue
                        label={t("admin.referral.rewardCode")}
                        value={<code>{detail.referrerRewardCode}</code>}
                      />
                      <LabelValue
                        label={t("admin.referral.rewardUsed")}
                        value={
                          detail.referrerRewardUsed
                            ? t("admin.referral.yes")
                            : t("admin.referral.no")
                        }
                      />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.referral.noRewardCode")}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("admin.referral.refereeReward")}
                  </Typography>
                  {detail.refereeRewardCode ? (
                    <>
                      <LabelValue
                        label={t("admin.referral.rewardCode")}
                        value={<code>{detail.refereeRewardCode}</code>}
                      />
                      <LabelValue
                        label={t("admin.referral.rewardUsed")}
                        value={
                          detail.refereeRewardUsed
                            ? t("admin.referral.yes")
                            : t("admin.referral.no")
                        }
                      />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.referral.noRewardCode")}
                    </Typography>
                  )}
                </Grid>
                {detail.rewardsExpiresAt && (
                  <Grid item xs={12}>
                    <LabelValue
                      label={t("admin.referral.rewardsExpiry")}
                      value={new Date(detail.rewardsExpiresAt).toLocaleDateString()}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {detail?.status === "Pending" && (
          <Button
            color="error"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t("admin.referral.cancelReferral")}
          </Button>
        )}
        <Button onClick={onClose}>{t("admin.referral.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReferralViewModal;
